import {
  memo,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInputForm } from 'components/dataCreationForms/withInputForm';
import { useAtomicPersist } from 'store/inspection';
import { StatefulComponentProps } from './types';
import { findDependencies, useDependsOn, useAutoSelect } from './model';
import { useDebouncedCallback } from 'helpers/misc';
import { toArrayValue } from 'shared/utils/array';

function StatefulComponent({
  Component,
  formKey,
  formValuesGetter: fieldFormValuesGetter,
  ...fieldProps
}: StatefulComponentProps): ReactElement {
  const didMountRef = useRef(false);
  const valueRef = useRef<unknown>(undefined);

  const { errors, values, setValues, registerField, setInputValues } =
    useInputForm();
  const { persistField } = useAtomicPersist();

  const valuesRef = useRef(values);
  valuesRef.current = values;

  const defaultFormValuesGetter = useCallback(
    () => ({ values: valuesRef.current }),
    []
  );
  const formValuesGetter = fieldFormValuesGetter ?? defaultFormValuesGetter;
  const componentRefFunction = useCallback(
    // react 18 types
    // @ts-ignore
    (component) => {
      registerField(
        formKey,
        component,
        fieldProps.labelId,
        fieldProps.dependsOn
      );
    },
    [registerField, formKey, fieldProps.labelId, fieldProps.dependsOn]
  );

  const [value, setValue] = useState(values[formKey]);

  const autoSelect = fieldProps.autoSelect;

  const defaultValue = useMemo(
    () => values[formKey] || fieldProps.defaultValue,
    [] // Empty deps - only compute on mount
  );

  const decidingFieldValue = fieldProps.dependsOn
    ? values[fieldProps.dependsOn]
    : undefined;

  const available = useMemo(
    () => findDependencies(
      fieldProps.dependsOn,
      fieldProps.extractor,
      fieldProps.available,
      decidingFieldValue
    ) || fieldProps.available,
    [fieldProps.dependsOn, fieldProps.extractor, fieldProps.available, decidingFieldValue]
  );

  const debounce = Boolean(fieldProps.debounce);

  const timeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // value from form should always overwrite local value
    const valueInForm = values[formKey];
    setValue(valueInForm);
    valueRef.current = valueInForm;
  }, [values, formKey]);

  const persistAtomicSave = useCallback(
    (nextValue: unknown): void => {
      if (!didMountRef.current) {
        return;
      }

      persistField(formKey, nextValue);
    },
    [formKey, persistField]
  );

  useEffect(() => {
    if (didMountRef.current) {
      // this prevents form edit on automatically populated multiselects.
      if (value === valueRef.current) {
        return;
      }
      setValues(formKey, value);
      persistAtomicSave(value);
    }
  }, [formKey, value, setValues, persistAtomicSave]);

  const inputValue = fieldProps.inputValue;

  // Reports the field's pending input to the parent form, which blocks submitting while it is not empty.
  useEffect(() => {
    setInputValues(formKey, inputValue);
  }, [setInputValues, formKey, inputValue]);

  useDependsOn(
    fieldProps.dependsOn,
    values,
    formKey,
    setValues,
    valueRef,
    didMountRef
  );

  useEffect(() => {
    didMountRef.current = true;
  }, []);

  const onSelection = useCallback(
    (_: unknown, selectedValues: unknown): void => {
      setValue(selectedValues);
      setValues(formKey, selectedValues);
      persistAtomicSave(selectedValues);
    },
    [setValue, setValues, formKey, persistAtomicSave]
  );

  const debouncedOnSelection = useDebouncedCallback(
    timeoutRef,
    onSelection,
    200
  );

  const propagateToForm = useCallback(
    (change: unknown): void => {
      setValues(formKey, change);
      persistAtomicSave(change);
    },
    [formKey, setValues, persistAtomicSave]
  );

  const debouncedPropagateToForm = useDebouncedCallback(
    timeoutRef,
    propagateToForm,
    200
  );

  const onChange = useCallback(
    (_: unknown, change: unknown): void => {
      fieldProps.handleChange?.(_, change);
      valueRef.current = change;
      setValue(change);
      if (debounce) {
        debouncedPropagateToForm(change);
      } else {
        setValues(formKey, change);
        persistAtomicSave(change);
      }
    },
    [fieldProps, formKey, setValues, debounce, debouncedPropagateToForm, persistAtomicSave]
  );

  const onDelete = useCallback(
    (_: unknown, __: unknown, valueToDelete: unknown): void => {
      const currentValue = toArrayValue(valueRef.current ?? value);
      const newValue = currentValue.filter((val) => val !== valueToDelete);
      valueRef.current = newValue;
      setValue(newValue);
      setValues(formKey, newValue);
      persistAtomicSave(newValue);
    },
    [value, setValue, setValues, formKey, persistAtomicSave]
  );
  const debouncedOnDelete = useDebouncedCallback(
    timeoutRef,
    onDelete,
    200
  );

  useAutoSelect(
    autoSelect,
    value,
    Array.isArray(available) ? available : undefined,
    setValue
  );

  // TODO: make fields properly show disability with tooltip and right reason.
  const isDisabled = fieldProps.disabled; //shouldDisable(fieldProps.disabled, dependencies);

  return (
    <Component
      {...fieldProps}
      ref={componentRefFunction}
      available={available}
      disabled={isDisabled}
      error={errors[formKey]}
      value={value || null}
      defaultValue={defaultValue}
      onSelection={debounce ? debouncedOnSelection : onSelection}
      onDelete={debounce ? debouncedOnDelete : onDelete}
      handleChange={onChange}
      formValuesGetter={formValuesGetter}
    />
  );
}

export const MemoStatefulFormInputComponent = memo(StatefulComponent);
